import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_2af900fb-a4c0-4108-9990-fef980765d9c/artifacts/8b0zrav9_LAFAYETTE_LOGO-04.jpg';

const VisitorCounter = ({ label }) => {
  const [count, setCount] = useState(null);

  useEffect(() => {
    const namespace = 'lafayette-international-school';
    const key = 'homepage-v1';
    let cancelled = false;
    const alreadyCounted = sessionStorage.getItem('lafayette_visit_counted');
    const endpoint = alreadyCounted ? 'get' : 'hit';

    fetch(`https://abacus.jasoncameron.dev/${endpoint}/${namespace}/${key}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data || typeof data.value !== 'number') return;
        if (!alreadyCounted) sessionStorage.setItem('lafayette_visit_counted', '1');
        setCount(data.value);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  if (count === null) return null;

  return (
    <div
      data-testid="footer-visitor-counter"
      className="flex items-center gap-2 font-inter text-slate-500 text-sm select-none"
      title={label}
      aria-label={`${count.toLocaleString('fr-FR')} ${label}`}
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" />
      </span>
      <span className="tracking-wider">
        <span className="text-slate-300 font-medium tabular-nums">
          {count.toLocaleString('fr-FR')}
        </span>{' '}
        <span className="text-slate-500">{label}</span>
      </span>
    </div>
  );
};

export const Footer = () => {
  const { t } = useTranslation();

  const footerLinks = {
    programs: [
      { label: t('footer.linksPrograms.french'), href: '#programs' },
      { label: t('footer.linksPrograms.english'), href: '#programs' },
      { label: t('footer.linksPrograms.spanish'), href: '#programs' },
    ],
    school: [
      { label: t('footer.linksSchool.about'), href: '#about' },
      { label: t('footer.linksSchool.values'), href: '#values' },
      { label: t('footer.linksSchool.environment'), href: '#campus' },
    ],
    admissions: [
      { label: t('footer.linksAdmissions.apply'), href: '#contact' },
      { label: t('footer.linksAdmissions.process'), href: '#admissions' },
      { label: t('footer.linksAdmissions.visit'), href: '#contact' },
    ],
  };

  const scrollToSection = (href) => {
    if (href === '#') return;
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer data-testid="footer" className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <motion.a
              href="#"
              data-testid="footer-logo"
              className="flex items-center gap-3 mb-6"
              whileHover={{ scale: 1.02 }}
            >
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center overflow-hidden">
                <img
                  src={LOGO_URL}
                  alt="Lafayette logo"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div>
                <span className="font-playfair font-bold text-xl block leading-tight">Lafayette</span>
                <span className="text-slate-400 text-xs tracking-wider">INTERNATIONAL SCHOOL</span>
              </div>
            </motion.a>
            <p className="font-inter text-slate-400 leading-relaxed mb-6 max-w-sm">
              {t('footer.tagline')}
            </p>
            <div className="font-inter text-slate-500 text-sm space-y-1">
              <p>10 Avenue Georges Clemenceau, 06000 Nice</p>
              <p>
                <a href="tel:+33493620029" className="hover:text-amber-400 transition-colors">
                  04 93 62 00 29
                </a>
              </p>
              <p>
                <a href="mailto:collegelyceelafayette@gmail.com" className="hover:text-amber-400 transition-colors">
                  collegelyceelafayette@gmail.com
                </a>
              </p>
            </div>
          </div>

          <div>
            <h4 className="font-playfair font-semibold text-lg mb-4">{t('footer.programs')}</h4>
            <ul className="space-y-3">
              {footerLinks.programs.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    data-testid={`footer-link-programs-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="font-inter text-slate-400 hover:text-white transition-colors text-sm text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-playfair font-semibold text-lg mb-4">{t('footer.school')}</h4>
            <ul className="space-y-3">
              {footerLinks.school.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    data-testid={`footer-link-school-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="font-inter text-slate-400 hover:text-white transition-colors text-sm text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-playfair font-semibold text-lg mb-4">{t('footer.admissions')}</h4>
            <ul className="space-y-3">
              {footerLinks.admissions.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    data-testid={`footer-link-admissions-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="font-inter text-slate-400 hover:text-white transition-colors text-sm text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="font-inter text-slate-500 text-sm">
              © {new Date().getFullYear()} {t('footer.copyright')}
            </p>
            <div className="flex items-center gap-4">
              <button
                data-testid="footer-privacy"
                className="font-inter text-slate-500 hover:text-white transition-colors text-sm"
              >
                {t('footer.privacy')}
              </button>
              <span className="text-slate-700">|</span>
              <VisitorCounter label={t('footer.visitors')} />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
